import { Box } from '@wix/design-system';
import React, { useEffect } from 'react';
import { useSelector, useStore } from 'react-redux';
import { matchRoutes, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { classNames, isSmartphone } from '../../../util';
import { Chatbot } from '../../chatbot';
import { updateHelpWidgetSession } from '../../common/api/updateHelpWidgetSession';
import { selectIsReset } from '../../common/selectors/chat';
import { selectFullPageSidebarExtended } from '../../common/selectors/helpCenter';
import { selectWidgetSessionState } from '../../common/selectors/widgetSession';
import { HeaderFullPage } from '../../help-center/components/HeaderFullPage';
import { MobileLandscape } from '../../help-center/components/MobileLandscape';
import { usePresentedState } from '../hooks';
import { AppRoutePatterns, appRoutes } from '../index';
import style from '../styles.scss';

// TODO:update routes
const articleRoutes = [
  { path: AppRoutePatterns.ArticleByUri },
  {
    path: AppRoutePatterns.ArticleById,
  },
];
const FULL_PAGE_CLASSNAME = 'full-page-container';
const FULL_PAGE_SMARTPHONE_CLASSNAME = 'full-page-smartphone-container';
const FULL_PAGE_SIDEBAR_CLASSNAME = 'full-page-container_with-sidebar';
const FULL_PAGE_EXTENDED_SIDEBAR_CLASSNAME = 'full-page-container_with-extended-sidebar';

export const FullPageLayout: React.FC = () => {
  const { sessionTabId } = useSelector(selectWidgetSessionState);
  const sidebarExtended = useSelector(selectFullPageSidebarExtended);
  const isChatReset = useSelector(selectIsReset);
  const store = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const matchedRoute = matchRoutes(articleRoutes, location);
  const isSidebarOpened = !!matchedRoute;

  usePresentedState();

  useEffect(() => {
    // we should open chatbot directly on any tab if user open Full(Contact) Page
    void updateHelpWidgetSession({
      id: sessionTabId,
      addedUrl: appRoutes.chatbot(),
    });
  }, []);

  useEffect(() => {
    const closeSidebar = (): void => {
      if (isSidebarOpened) {
        navigate(appRoutes.chatbot());
      }
    };

    if (isChatReset) {
      closeSidebar();
    }
  }, [isChatReset]);

  const sidebarStateClassName = sidebarExtended
    ? FULL_PAGE_EXTENDED_SIDEBAR_CLASSNAME
    : FULL_PAGE_SIDEBAR_CLASSNAME;

  const mixinClassName = classNames([
    FULL_PAGE_CLASSNAME,
    isSmartphone && FULL_PAGE_SMARTPHONE_CLASSNAME,
    isSidebarOpened && sidebarStateClassName,
  ]);

  return (
    <Box
      className={mixinClassName}
      direction="vertical"
      flexGrow={1}
      height="100%"
      minWidth={isSmartphone ? '100%' : '768px'}
    >
      <HeaderFullPage />
      <Box position="relative" flexGrow={1} overflow="hidden">
        <Chatbot store={store} />
        <Box className={style.fullPageSidebar}>
          <Outlet />
        </Box>
      </Box>
      {isSmartphone && <MobileLandscape />}
    </Box>
  );
};
